'use client';

import { FloatingOverlay } from '@floating-ui/react';
import { forwardRef, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { useBodyScrollLock } from '../../hooks/use-body-scroll-lock';

import { DrawerFloatingContext } from './context/drawer-floating.context';
import { DrawerRootContext } from './context/drawer-root.context';
import { drawerBackdropRecipe } from './styles/drawer-backdrop.styles';
import type { DrawerBackdropPropsInterface } from './types/drawer-backdrop.types';

/**
 * @since 10.22.0
 */
export const DrawerBackdrop = forwardRef<HTMLDivElement, DrawerBackdropPropsInterface>(
  (props: DrawerBackdropPropsInterface, forwardedRef) => {
    const { contained, placement } = useContext(DrawerRootContext);
    const { status } = useContext(DrawerFloatingContext);
    const {
      children,
      className,
      css: cssProp,
      lockScroll = true,
      scrim = false,
      style,
      ...rest
    } = props;

    // The hook's effect keeps scrolling locked through the backdrop's exit transition.
    useBodyScrollLock(!contained && lockScroll);

    const styles = css(
      drawerBackdropRecipe.raw({ contained, open: status === 'open', placement, scrim }),
      cssProp,
    );

    return (
      <FloatingOverlay
        ref={forwardedRef}
        data-c11n-version={__C11N_VERSION__}
        data-c11n-component="Drawer.Backdrop"
        className={cx('c11n', 'c11n_drawer__backdrop', styles, className)}
        style={{
          ...style,
          /*
           * `FloatingOverlay` hardcodes `position` and `overflow` inline, which no
           * class can override, so both have to be set here. Clipping keeps the
           * closed panel from growing the host's scrollable area.
           */
          ...(contained && { position: 'absolute' }),
          overflow: 'clip',
        }}
        {...rest}
      >
        {children}
      </FloatingOverlay>
    );
  },
);

DrawerBackdrop.displayName = 'Drawer.Backdrop';
